// src/components/AuctionCommishControls.tsx
//
// The commissioner's auction operations panel, rendered ON /auction (gated to
// the commissioner) so running the room never needs a second tab. Moved from
// /commish per commissioner direction 2026-08-02; season admin (rollover,
// cutdown lock) stays on /commish.
//
// Self-contained: owns its queries/mutations/state so the page just renders
// <AuctionCommishControls /> behind an isCommissioner gate.
import { useMutation, useQuery } from 'convex/react';
import { type FormEvent, useState } from 'react';
import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';

export function AuctionCommishControls() {
  const currentSeason = useQuery(api.seasons.getCurrent);
  const auction = useQuery(api.rfa.init.getCurrentAuction);
  // A COMPLETED mock falls off getCurrentAuction (routing hides complete
  // rows) but must stay resettable — this query keeps the button reachable.
  const resettableMock = useQuery(api.rfa.init.getResettableMock);
  // Live-only: backs the zero-bid confirm on "Close bidding" (Phase 6g — a
  // bidless close retains the player with the prior owner at 0).
  const liveView = useQuery(
    api.rfa.auction.getAuctionView,
    auction && auction.status === 'live' ? { auctionId: auction._id } : 'skip',
  );

  const initialize = useMutation(api.rfa.init.initializeAuction);
  const resetMock = useMutation(api.rfa.init.resetMockAuction);
  const lockTags = useMutation(api.rfa.tags.lockTags);
  const closeBidding = useMutation(api.rfa.auction.closeBidding);
  const pauseAuction = useMutation(api.rfa.auction.pauseAuction);
  const resumeAuction = useMutation(api.rfa.auction.resumeAuction);
  const closeAuction = useMutation(api.rfa.close.closeAuction);

  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [seasonInput, setSeasonInput] = useState<number | null>(null);
  const [matchClockSeconds, setMatchClockSeconds] = useState<number>(300);
  const [bidClockSeconds, setBidClockSeconds] = useState<number>(60);
  const [mockMode, setMockMode] = useState(false);
  const [mockPoolSize, setMockPoolSize] = useState<number>(20);
  const [pauseReason, setPauseReason] = useState<string>('break');
  const [pauseMinutes, setPauseMinutes] = useState<string>('');

  const effectiveSeason = seasonInput ?? currentSeason?.year ?? null;

  // Pool preview only matters while there is no auction to run.
  const pool = useQuery(
    api.rollover.getRfaPool,
    auction === null && effectiveSeason !== null ? { season: effectiveSeason } : 'skip',
  );

  const run = async (fn: () => Promise<unknown>) => {
    setError(null);
    setSubmitting(true);
    try {
      await fn();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const onInit = async (e: FormEvent) => {
    e.preventDefault();
    if (effectiveSeason === null) return;
    // Never submit an empty/zero pool size — fall back to the default 20
    // (the server enforces 1..60 with INVALID_MOCK_POOL_SIZE).
    const safePoolSize =
      Number.isInteger(mockPoolSize) && mockPoolSize >= 1 && mockPoolSize <= 60 ? mockPoolSize : 20;
    await run(() =>
      initialize({
        season: effectiveSeason,
        matchClockSeconds,
        bidClockSeconds,
        ...(mockMode ? { mock: true, mockPoolSize: safePoolSize } : {}),
      }),
    );
  };

  const onResetMock = async (mockAuctionId: Id<'rfaAuctions'>) => {
    const ok = window.confirm(
      "Deletes this mock auction and all its bids/results — the league's real data is untouched.",
    );
    if (!ok) return;
    await run(() => resetMock({ auctionId: mockAuctionId }));
  };

  const onPause = async () => {
    if (!auction) return;
    const minutes = pauseMinutes.trim() === '' ? undefined : Number(pauseMinutes);
    await run(() =>
      pauseAuction({ auctionId: auction._id, reason: pauseReason, durationMinutes: minutes }),
    );
  };

  const onCloseBidding = async () => {
    if (!auction) return;
    // Zero-bid close (Phase 6g): no match window — the player is retained by
    // the prior owner at 0. Confirm so a premature close isn't silent.
    if (liveView?.currentPlayer && liveView.currentPlayer.topBid === null) {
      const ok = window.confirm(
        'No bids — player will be retained by the prior owner at 0. Close bidding?',
      );
      if (!ok) return;
    }
    await run(() => closeBidding({ auctionId: auction._id }));
  };

  const onCloseAuction = async () => {
    if (!auction) return;
    const ok = window.confirm(
      auction.isMock === true
        ? 'Close the mock auction? Real bones balances are untouched (it was never real).'
        : 'Close the auction? This is irreversible: every franchise bones balance resets to 75.',
    );
    if (!ok) return;
    await run(() => closeAuction({ auctionId: auction._id }));
  };

  // Group the pool preview by prior franchise (entries arrive sorted by
  // priorFranchiseName then playerName).
  const poolGroups = new Map<string, NonNullable<typeof pool>>();
  for (const entry of pool ?? []) {
    const group = poolGroups.get(entry.priorFranchiseName) ?? [];
    group.push(entry);
    poolGroups.set(entry.priorFranchiseName, group);
  }

  const status = auction?.status;
  const canLockTags = status === 'pre';
  // liveView must have resolved so a fast click can't skip the zero-bid confirm.
  const canCloseBidding =
    status === 'live' && auction?.currentPlayerId !== undefined && liveView !== undefined;
  const canPause = status === 'live' || status === 'awaiting_match';
  const canResume = status === 'paused';
  const canCloseAuction = auction !== null && auction !== undefined && status !== 'paused';

  return (
    <section className="rounded border-2 border-blue-200 bg-blue-50/40 p-4">
      <h2 className="font-semibold">Commissioner controls</h2>

      {error && (
        <div className="mt-3 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {auction === undefined ? (
        <p className="mt-3 text-sm text-gray-500">Loading auction status…</p>
      ) : auction === null ? (
        <>
          {/* Completed mock from this season — no longer the "current"
              auction, but still occupying the season until it's reset. */}
          {resettableMock != null && (
            <div className="mt-3 rounded border border-amber-300 bg-amber-50 p-4 shadow-sm">
              <h3 className="font-semibold">
                Completed mock auction from this season
                <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs font-semibold text-amber-800 align-middle">
                  MOCK
                </span>
              </h3>
              <p className="mt-1 text-sm text-gray-600">
                A finished mock ({resettableMock.season}) is still stored. Reset it to clear the dry
                run — the league's real data is untouched either way.
              </p>
              <button
                type="button"
                onClick={() => onResetMock(resettableMock._id)}
                disabled={submitting}
                className="mt-2 rounded border border-amber-400 bg-white px-3 py-2 text-sm text-amber-800 disabled:opacity-50"
              >
                Reset mock auction
              </button>
            </div>
          )}

          {/* RFA pool preview */}
          <div className="mt-3 rounded border bg-white p-4 shadow-sm">
            <h3 className="font-semibold">
              RFA pool preview{effectiveSeason !== null ? ` — ${effectiveSeason}` : ''}
            </h3>
            {pool === undefined ? (
              <p className="mt-2 text-sm text-gray-500">Loading…</p>
            ) : pool.length === 0 ? (
              <p className="mt-2 text-sm text-gray-500">
                No RFA-eligible players derived — run season rollover first (on /commish).
              </p>
            ) : (
              <div className="mt-2 space-y-3">
                {[...poolGroups.entries()].map(([franchiseNameKey, entries]) => (
                  <div key={franchiseNameKey}>
                    <div className="text-sm font-medium">{franchiseNameKey}</div>
                    <ul className="mt-0.5 text-sm text-gray-700">
                      {entries.map((p) => (
                        <li key={p.playerId}>
                          {p.playerName}{' '}
                          <span className="text-xs text-gray-500">
                            ({p.position} {p.nflTeam})
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Init form */}
          <form onSubmit={onInit} className="mt-3 rounded border bg-white p-4 shadow-sm">
            <h3 className="font-semibold">Initialize auction</h3>
            <p className="mt-1 text-xs text-gray-500">
              Eligibility is derived server-side from the pool above.
            </p>
            <div className="mt-2 flex flex-wrap items-end gap-3">
              <label className="flex flex-col text-sm">
                <span className="text-gray-600 mb-1">Season</span>
                <input
                  type="number"
                  className="border rounded px-2 py-1 w-24"
                  value={effectiveSeason ?? ''}
                  onChange={(e) => setSeasonInput(Number(e.target.value))}
                />
              </label>
              <label className="flex flex-col text-sm">
                <span className="text-gray-600 mb-1">Match clock (s)</span>
                <input
                  type="number"
                  min={1}
                  className="border rounded px-2 py-1 w-24"
                  value={matchClockSeconds}
                  onChange={(e) => setMatchClockSeconds(Number(e.target.value))}
                />
              </label>
              <label className="flex flex-col text-sm">
                <span className="text-gray-600 mb-1">Bid clock (s)</span>
                <input
                  type="number"
                  min={1}
                  className="border rounded px-2 py-1 w-24"
                  value={bidClockSeconds}
                  onChange={(e) => setBidClockSeconds(Number(e.target.value))}
                />
              </label>
              <button
                type="submit"
                disabled={submitting || effectiveSeason === null}
                className="rounded bg-blue-600 px-3 py-2 text-sm text-white disabled:opacity-50"
              >
                {submitting
                  ? 'Initializing…'
                  : mockMode
                    ? 'Start mock auction'
                    : 'Initialize auction'}
              </button>
            </div>
            <div className="mt-3 border-t pt-3">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={mockMode}
                  onChange={(e) => setMockMode(e.target.checked)}
                />
                <span className="font-medium">Mock auction (practice)</span>
              </label>
              <p className="mt-1 text-xs text-gray-500">
                Runs before rollover using sampled rosters. Nothing is real: no contracts, no bones
                — reset it any time for another dry run.
              </p>
              {mockMode && (
                <label className="mt-2 flex flex-col text-sm w-fit">
                  <span className="text-gray-600 mb-1">Pool size (if sampling, 1–60)</span>
                  <input
                    type="number"
                    min={1}
                    max={60}
                    className="border rounded px-2 py-1 w-24"
                    value={mockPoolSize}
                    onChange={(e) => setMockPoolSize(Number(e.target.value))}
                  />
                </label>
              )}
            </div>
          </form>
        </>
      ) : (
        <div className="mt-3">
          <div className="flex flex-wrap gap-4 text-sm">
            <span>
              Nomination cursor:{' '}
              <strong>
                {auction.nominationOrder.length > 0
                  ? `${auction.nominationCursor + 1} / ${auction.nominationOrder.length}`
                  : '—'}
              </strong>
            </span>
            <span>
              Clocks: bid <strong>{auction.bidClockSeconds}s</strong> · match{' '}
              <strong>{auction.matchClockSeconds}s</strong>
            </span>
          </div>

          <div className="mt-3 flex flex-wrap items-end gap-3">
            <button
              type="button"
              onClick={() => run(() => lockTags({ auctionId: auction._id }))}
              disabled={submitting || !canLockTags}
              className="rounded bg-blue-600 px-3 py-2 text-sm text-white disabled:opacity-50"
            >
              Lock tags
            </button>
            <button
              type="button"
              onClick={onCloseBidding}
              disabled={submitting || !canCloseBidding}
              className="rounded bg-blue-600 px-3 py-2 text-sm text-white disabled:opacity-50"
            >
              Close bidding
            </button>
            <button
              type="button"
              onClick={() => run(() => resumeAuction({ auctionId: auction._id }))}
              disabled={submitting || !canResume}
              className="rounded bg-blue-600 px-3 py-2 text-sm text-white disabled:opacity-50"
            >
              Resume
            </button>
            <button
              type="button"
              onClick={onCloseAuction}
              disabled={submitting || !canCloseAuction}
              className="rounded border border-red-300 px-3 py-2 text-sm text-red-700 disabled:opacity-50"
            >
              Close auction
            </button>
            {auction.isMock === true && (
              <button
                type="button"
                onClick={() => onResetMock(auction._id)}
                disabled={submitting}
                className="rounded border border-amber-400 bg-amber-50 px-3 py-2 text-sm text-amber-800 disabled:opacity-50"
              >
                Reset mock auction
              </button>
            )}
          </div>

          <div className="mt-3 flex flex-wrap items-end gap-3 border-t pt-3">
            <label className="flex flex-col text-sm">
              <span className="text-gray-600 mb-1">Pause reason</span>
              <select
                className="border rounded px-2 py-1"
                value={pauseReason}
                onChange={(e) => setPauseReason(e.target.value)}
              >
                <option value="break">Break</option>
                <option value="tag_over_budget">Tag over budget</option>
                <option value="tech issue">Tech issue</option>
              </select>
            </label>
            <label className="flex flex-col text-sm">
              <span className="text-gray-600 mb-1">Minutes (optional)</span>
              <input
                type="number"
                min={1}
                className="border rounded px-2 py-1 w-24"
                value={pauseMinutes}
                onChange={(e) => setPauseMinutes(e.target.value)}
              />
            </label>
            <button
              type="button"
              onClick={onPause}
              disabled={submitting || !canPause}
              className="rounded bg-amber-600 px-3 py-2 text-sm text-white disabled:opacity-50"
            >
              Pause
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
